import { CheckCircle2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { Button } from "@/components/ui/button";

import { GRAVUN_BRAND } from "@/branding";
import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { captureUtm, type FormFieldDef } from "./formModel";

interface PublicForm {
  slug: string;
  name: string;
  description: string | null;
  fields: FormFieldDef[];
  success_message: string;
  landing?: {
    headline?: string;
    subtext?: string;
    bullets?: string[];
  } | null;
}

const inputClass =
  "h-11 w-full rounded-[10px] border border-input bg-card px-3 text-[14px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * Public, unauthenticated lead-capture form (/#/f/:slug). Matches the
 * Gravun public-surface design (navy brand panel + white card). Rendering
 * comes from get_form; the submission goes through the anon-granted
 * submit_form RPC which validates server-side and applies attribution,
 * consent, and follow-up side effects.
 */
export const PublicFormPage = () => {
  const { slug = "" } = useParams();
  const [form, setForm] = useState<PublicForm | null | "missing">(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const utm = useMemo(() => captureUtm(window.location.search), []);

  useEffect(() => {
    (async () => {
      const { data } = await getSupabaseClient().rpc("get_form", {
        p_slug: slug,
      });
      setForm(data && data.length > 0 ? (data[0] as PublicForm) : "missing");
      // Count the view for form analytics; fire and forget, a failed
      // counter must never affect the visitor. NOTE: supabase-js builders
      // are lazy thenables, so the call must be subscribed or it never
      // sends ("void builder" silently does nothing).
      getSupabaseClient()
        .rpc("record_form_view", { p_slug: slug })
        .then(
          () => undefined,
          () => undefined,
        );
    })();
  }, [slug]);

  const setValue = (key: string, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const submit = async () => {
    if (form === null || form === "missing") return;
    setSubmitting(true);
    setError(null);
    const { data, error: rpcError } = await getSupabaseClient().rpc(
      "submit_form",
      { p_slug: slug, p_data: values, p_utm: utm },
    );
    setSubmitting(false);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    const result = (
      data as { success_message: string; redirect_url: string | null }[]
    )[0];
    if (result.redirect_url) {
      window.location.href = result.redirect_url;
      return;
    }
    setDone(result.success_message);
  };

  const visibleFields =
    form !== null && form !== "missing"
      ? form.fields.filter((f) => f.type !== "hidden")
      : [];

  return (
    <div className="flex min-h-screen">
      <div className="relative hidden flex-col justify-between bg-[oklch(0.22_0.055_262)] p-12 text-white lg:flex lg:w-[38%]">
        <img
          className="h-9 w-auto self-start"
          src={GRAVUN_BRAND.wordmarkWhite}
          alt={GRAVUN_BRAND.name}
        />
        <div className="max-w-sm space-y-3">
          <h2 className="text-3xl font-bold leading-tight tracking-tight">
            {(form !== null && form !== "missing" && form.landing?.headline) ||
              "Tell us what you need."}
          </h2>
          <p className="text-base text-white/60">
            {(form !== null && form !== "missing" && form.landing?.subtext) ||
              "Send the details and the team will get right back to you."}
          </p>
          {form !== null &&
          form !== "missing" &&
          (form.landing?.bullets ?? []).filter((b) => b.trim()).length > 0 ? (
            <ul className="space-y-2 pt-2">
              {(form.landing?.bullets ?? [])
                .filter((b) => b.trim())
                .map((bullet) => (
                  <li
                    key={bullet}
                    className="flex items-start gap-2 text-[14.5px] text-white/80"
                  >
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-white/60" />
                    {bullet}
                  </li>
                ))}
            </ul>
          ) : null}
        </div>
        <p className="text-sm text-white/40">
          © {new Date().getFullYear()} {GRAVUN_BRAND.name} Inc.
        </p>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center bg-background p-4 lg:p-10">
        <div className="w-full max-w-xl">
          {form === null ? null : form === "missing" ? (
            <div className="rounded-2xl border border-border-subtle bg-card p-8 text-center">
              <h1 className="text-xl font-semibold">
                This form is unavailable
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                The link may be wrong or the form has been turned off.
              </p>
            </div>
          ) : done ? (
            <div className="rounded-2xl border border-border-subtle bg-card p-8 text-center">
              <CheckCircle2 className="mx-auto size-10 text-success" />
              <h1 className="mt-3 text-2xl font-semibold tracking-tight">
                Request received
              </h1>
              <p className="mt-2 text-[14.5px] text-muted-foreground">{done}</p>
            </div>
          ) : (
            <div className="rounded-2xl border border-border-subtle bg-card p-8">
              <img
                className="mb-6 h-7 w-auto lg:hidden dark:hidden"
                src={GRAVUN_BRAND.wordmarkNavy}
                alt={GRAVUN_BRAND.name}
              />
              <h1 className="text-2xl font-semibold tracking-tight">
                {form.name}
              </h1>
              {form.description ? (
                <p className="mt-1 text-sm text-muted-foreground">
                  {form.description}
                </p>
              ) : null}

              <div className="mt-6 flex flex-col gap-4">
                {visibleFields.map((field) => (
                  <PublicField
                    key={field.key}
                    field={field}
                    value={values[field.key] ?? ""}
                    onChange={(value) => setValue(field.key, value)}
                  />
                ))}
                {error ? (
                  <p className="text-[13px] text-destructive">{error}</p>
                ) : null}
                <Button
                  size="lg"
                  className="w-full"
                  disabled={submitting}
                  onClick={submit}
                >
                  {submitting ? "Sending…" : "Send request"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PublicField = ({
  field,
  value,
  onChange,
}: {
  field: FormFieldDef;
  value: string;
  onChange: (value: string) => void;
}) => {
  const label = (
    <span className="text-[13px] font-medium text-foreground">
      {field.label ?? field.key}
      {field.required ? <span className="text-destructive"> *</span> : null}
    </span>
  );

  if (
    field.type === "checkbox" ||
    field.type === "consent_email" ||
    field.type === "consent_sms"
  ) {
    return (
      <label className="flex items-center gap-2.5 text-[13.5px]">
        <input
          type="checkbox"
          className="size-4 accent-primary"
          checked={value === "true"}
          onChange={(e) => onChange(e.target.checked ? "true" : "")}
        />
        {field.label ?? field.key}
      </label>
    );
  }

  if (field.type === "textarea") {
    return (
      <label className="flex flex-col gap-1.5">
        {label}
        <textarea
          className="min-h-24 w-full rounded-[10px] border border-input bg-card px-3 py-2 text-[14px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
          placeholder={field.placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
    );
  }

  if (field.type === "select") {
    return (
      <label className="flex flex-col gap-1.5">
        {label}
        <select
          className={inputClass}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Choose…</option>
          {(field.options ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>
    );
  }

  return (
    <label className="flex flex-col gap-1.5">
      {label}
      <input
        className={inputClass}
        type={
          field.type === "email"
            ? "email"
            : field.type === "phone"
              ? "tel"
              : "text"
        }
        placeholder={field.placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
};

PublicFormPage.path = "/f/:slug";
